// Menu — the week planner. Tap a slot to assign a recipe (picker overlay).
// The compiled shopping list (planned minus stock, plus hand-added items)
// lives in its own mode, Shopping, built for the phone; this view only
// shows the count and links there.

import { useState } from "react";
import useSWR from "swr";
import { ArrowRight, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import { fetcher, imageUrl, put } from "@/api/client";
import type { ShoppingItem } from "@/views/Shopping";
import { macros, type Nutrition } from "@/lib/nutrition";
import { cn } from "@/lib/utils";
import { playClick } from "@/sounds";

interface SlotEntry {
  recipe_id: number;
  name: string;
  image: string | null;
  servings: number;
  nutrition: Nutrition | null;
}

interface Day {
  date: string;
  slots: Record<"lunch" | "dinner", SlotEntry | null>;
}
interface RecipeLite {
  id: number;
  name: string;
  image: string | null;
}

const SLOTS = ["lunch", "dinner"] as const;
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Menu() {
  const { data: week, mutate: mutateWeek } = useSWR<{ days: Day[] }>("/api/menu", fetcher);
  const { data: shopping, mutate: mutateShopping } = useSWR<{ items: ShoppingItem[] }>(
    "/api/menu/shopping",
    fetcher,
  );
  const [picking, setPicking] = useState<{ date: string; slot: (typeof SLOTS)[number] } | null>(
    null,
  );
  const openCount = shopping?.items.filter((i) => !i.checked).length ?? 0;

  async function assign(recipeId: number | null) {
    if (!picking) return;
    await put("/api/menu", { date: picking.date, slot: picking.slot, recipe_id: recipeId });
    setPicking(null);
    void mutateWeek();
    void mutateShopping();
  }

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6">
      {/* week grid */}
      <section>
        {/* phone: step right of the rolled side-panel squircle */}
        <h2 className="mb-3 min-h-[calc(var(--rail-width)-8px)] pl-[calc(var(--rail-width)+4px)] font-mono text-[11px] uppercase leading-[calc(var(--rail-width)-8px)] tracking-[0.14em] text-brand-ink md:min-h-0 md:pl-0 md:leading-normal">
          this week
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {(week?.days ?? []).map((day) => {
            const d = new Date(`${day.date}T12:00:00`);
            const dayKcal = SLOTS.reduce((sum, s) => sum + (day.slots[s]?.nutrition?.kcal ?? 0), 0);
            return (
              <div key={day.date} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between px-1 font-mono text-[11px] text-dim">
                  <span>
                    {WEEKDAY[d.getDay()]} {d.getDate()}.{d.getMonth() + 1}.
                  </span>
                  {dayKcal > 0 && <span className="text-dimmer">{dayKcal} kcal</span>}
                </div>
                {SLOTS.map((slot) => {
                  const entry = day.slots[slot];
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        setPicking({ date: day.date, slot });
                        playClick();
                      }}
                      className={cn(
                        "relative flex h-24 cursor-pointer flex-col items-stretch overflow-hidden rounded-xl border text-left",
                        "transition-transform duration-500 ease-epicure hover:-translate-y-0.5",
                        entry ? "border-line bg-surface" : "border-dashed border-line-strong",
                        entry && imageUrl(entry.image) ? "justify-end" : "justify-center",
                      )}
                    >
                      {entry ? (
                        imageUrl(entry.image) ? (
                          <>
                            <img
                              src={imageUrl(entry.image)!}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                            <span className="relative bg-gradient-to-t from-black/80 to-transparent p-1.5 pt-6 text-[#f5f2e8]">
                              <span className="block text-[11px] leading-tight font-semibold">{entry.name}</span>
                              {entry.nutrition && (
                                <span className="mt-0.5 block font-mono text-[9px] text-[#f5f2e8]/70">
                                  {entry.nutrition.kcal} kcal · {macros(entry.nutrition)}
                                </span>
                              )}
                            </span>
                          </>
                        ) : (
                          /* no picture: the dish, centred, with its numbers */
                          <span className="flex flex-col items-center justify-center gap-1 px-2 text-center">
                            <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-dimmer">{slot}</span>
                            <span className="line-clamp-2 font-display text-[13px] leading-tight text-foreground">
                              {entry.name}
                            </span>
                            {entry.nutrition ? (
                              <span className="font-mono text-[9px] text-dim">
                                {entry.nutrition.kcal} kcal · {macros(entry.nutrition)}
                              </span>
                            ) : (
                              <span className="font-mono text-[9px] text-dimmer">{entry.servings} servings</span>
                            )}
                          </span>
                        )
                      ) : (
                        <span className="flex flex-1 items-center justify-center gap-1 text-[11px] text-dimmer">
                          <Plus className="size-3" /> {slot}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </section>

      {/* shopping list — the count, and the way to the cart mode */}
      <section>
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-ink">
          shopping list <span className="text-dim normal-case tracking-normal">— planned minus stock</span>
        </h2>
        <Link
          to="/shopping"
          onClick={() => playClick()}
          className="flex max-w-xl cursor-pointer items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 text-sm transition-transform duration-500 ease-epicure hover:-translate-y-0.5"
        >
          <span className="min-w-0 flex-1 truncate">
            {!shopping
              ? "…"
              : openCount === 0
                ? "Nothing to buy."
                : `${openCount} to buy · ${shopping.items
                    .filter((i) => !i.checked)
                    .slice(0, 6)
                    .map((i) => i.name)
                    .join(", ")}${openCount > 6 ? ", …" : ""}`}
          </span>
          <ArrowRight className="size-4 shrink-0 text-dim" />
        </Link>
      </section>

      {picking && <RecipePicker onPick={assign} onClose={() => setPicking(null)} />}
    </div>
  );
}

function RecipePicker({
  onPick,
  onClose,
}: {
  onPick: (recipeId: number | null) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const { data: recipes } = useSWR<RecipeLite[]>(
    `/api/recipes?query=${encodeURIComponent(query)}`,
    fetcher,
  );

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 p-6" onClick={onClose}>
      <div
        className="flex max-h-[70vh] w-full max-w-md flex-col rounded-[var(--radius-card)] border border-line-strong bg-popover p-4 shadow-[0_15px_40px_rgba(0,0,0,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="search recipes"
          className="rounded-lg border border-line-strong bg-transparent px-3 py-2.5 text-sm outline-none focus:border-brand-ink"
        />
        <div className="mt-3 flex-1 overflow-y-auto">
          {(recipes ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onPick(r.id)}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-panel"
            >
              {imageUrl(r.image) ? (
                <img src={imageUrl(r.image)!} alt="" className="size-9 rounded-lg object-cover" />
              ) : (
                <span className="flex size-9 items-center justify-center rounded-lg bg-panel font-display italic">
                  {r.name.slice(0, 1)}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
            </button>
          ))}
          {recipes && recipes.length === 0 && (
            <p className="px-2 py-4 text-xs text-dim">no recipes match</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onPick(null)}
          className="mt-3 cursor-pointer rounded-full border border-line-strong px-4 py-2 text-xs font-semibold text-dim hover:text-foreground"
        >
          clear this slot
        </button>
      </div>
    </div>
  );
}
